/**
 * FollowButton - Follow/unfollow button for any page with a hub/drop entity
 *
 * Behavior varies by followMode:
 * - OPEN: toggles follow/unfollow (ACTIVE immediately)
 * - APPROVAL: shows "Request to Follow" → "Pending" after click
 * - INVITE_ONLY: hidden (no UI shown)
 *
 * Requires authentication — shows nothing if not logged in
 * (parent component should handle login prompt).
 */

import {ReactElement, useCallback, useState} from "react";
import {useSelector} from "react-redux";
import {FaCheck} from "react-icons/fa";
import {IStore} from "@/redux/defaultStore";
import {EntityType, TrackEvent, TrackSurface} from "@devour/client";
import {useGetDropFollowStatus, useFollowDrop, useUnfollowDrop} from "@/hooks/drop/useDropFollow";
import type {HubFollowMode} from "@/hooks/hub/useGetHub";
import {useTrackClick} from "@/hooks/useTrackClick";
import analytics from "@/utils/analytics";
import "./FollowButton.scss";

interface FollowButtonProps {
    /** Hub URL slug — drives Hub follow APIs when `dropSlug` is unset */
    slug: string;
    /** When set, this widget operates against Drop follow APIs instead of Hub */
    dropSlug?: string;
    /** Hub/Drop MongoDB ObjectId — used as entityId for Click tracking */
    surfaceId?: string;
    /** Follow mode — determines button behavior (OPEN/APPROVAL/INVITE_ONLY). Drops are always OPEN. */
    followMode?: HubFollowMode;
    /** Total follower count to display beside the button */
    followerCount?: number;
    /** Display name (used in followers modal header) */
    hubName?: string;
    /** Callback triggered when an unauthenticated user clicks follow */
    onLoginRequired?: () => void;
    /** Referrer user ID for follow attribution (from ?hubRef= / ?dropRef= query param) */
    referralUserId?: string;
}

export function FollowButton({
    slug,
    dropSlug,
    surfaceId,
    followMode = "OPEN",
    followerCount,
    hubName,
    onLoginRequired,
    referralUserId,
}: FollowButtonProps): ReactElement | null {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const isLoggedIn = !!fullToken?.token;

    const isDrop = Boolean(dropSlug);

    /*
     * One follow path. Hub is retired, so every surface follows through
     * DropFollow — `dropSlug ?? slug` covers callers that still pass the
     * generic slug prop.
     */
    const {data: followStatus} = useGetDropFollowStatus(dropSlug ?? slug);

    const dropFollowMut = useFollowDrop();
    const dropUnfollowMut = useUnfollowDrop();

    /*
     * Click tracking routes to the correct entity/surface per mode: Drop follows
     * land on EntityType.drop / DropPage, Hub follows on EntityType.hub / HubPage.
     * The button-press Click fires every time; a successful *new* drop follow
     * additionally fires TrackEvent.follow below to feed the `follows` counter.
     */
    const trackFollowClick = useTrackClick(
        isDrop ? EntityType.drop : EntityType.hub,
        surfaceId,
        isDrop ? TrackSurface.drop_page : TrackSurface.hub_page,
    );

    const [isHovered, setIsHovered] = useState(false);

    /*
     * Compare against string literals — HubFollowStatus and DropFollowStatus
     * are distinct enum types in the generated client but their string values
     * match (ACTIVE / PENDING / REJECTED), so direct comparison stays simple.
     */
    const isActive = followStatus?.status === "ACTIVE";
    const isPending = followStatus?.status === "PENDING";
    /*
     * One mutation pair for both surfaces — a Hub follow and a Drop follow used
     * to be separate hooks, and these lines were ternaries whose branches had
     * become identical once the Hub half was removed.
     */
    const isFollowing = dropFollowMut.isPending;
    const isUnfollowing = dropUnfollowMut.isPending;
    const isBusy = isFollowing || isUnfollowing;

    const handleClick = useCallback(async () => {
        trackFollowClick();
        if (!isLoggedIn) {
            onLoginRequired?.();
            return;
        }

        /*
         * mutateAsync rejects on network/4xx/5xx. onClick doesn't consume the
         * returned promise, so without this try/catch every failed follow/unfollow
         * surfaces as an unhandled promise rejection (routine on flaky connections).
         * The mutations own their optimistic-rollback via React Query onError; here
         * we just prevent the unhandled rejection and log.
         */
        try {
            if (isDrop && dropSlug) {
                if (isActive) {
                    await dropUnfollowMut.mutateAsync(dropSlug);
                } else if (!isPending) {
                    await dropFollowMut.mutateAsync({slug: dropSlug, dropRef: referralUserId});
                    /*
                     * Fire the Follow event only on a successful *new* follow (not
                     * unfollow) so the `follows` counter and follow-trend reflect net
                     * new follows. Keyed by the drop's ObjectId (surfaceId prop).
                     */
                    if (surfaceId) {
                        void analytics.track({
                            entityType: EntityType.drop,
                            entityId: surfaceId,
                            event: TrackEvent.follow,
                            surface: TrackSurface.drop_page,
                        });
                    }
                }
                return;
            }

            /*
             * No `dropSlug` — a caller that renders Hero without it (dropSlug is
             * optional there). `slug` is still a Drop slug, since Drop is the only
             * surviving surface, so this reaches the same follow endpoint; the
             * referral param is `dropRef`, and this passed `hubRef` until the Hub
             * follow mutation it was written against was removed.
             */
            if (isActive) {
                await dropUnfollowMut.mutateAsync(slug);
            } else if (!isPending) {
                await dropFollowMut.mutateAsync({slug, dropRef: referralUserId});
            }
        } catch (err) {
            console.error("[FollowButton] follow/unfollow failed:", err);
        }
    }, [isLoggedIn, isActive, isPending, slug, dropSlug, isDrop, surfaceId, referralUserId, dropFollowMut, dropUnfollowMut, onLoginRequired, trackFollowClick]);

    /*
     * Don't render for INVITE_ONLY hubs (does not apply to Drops — always OPEN).
     * MUST come after every hook above (incl. handleClick) — early-returning
     * before a hook is a Rules-of-Hooks violation that throws when followMode flips.
     */
    if (!isDrop && followMode === "INVITE_ONLY") return null;

    // Determine button text and style in a single pass to avoid flicker
    let buttonContent: ReactElement | string;
    let buttonClass = "follow-btn";

    if (isBusy) {
        // Busy state takes priority — prevents flicker when transitioning
        buttonContent = isActive ? "Unfollowing..." : "Following...";
        if (isActive) buttonClass += " follow-btn--following";
    } else if (isActive) {
        buttonClass += " follow-btn--following";
        if (isHovered) {
            buttonContent = "Unfollow";
        } else {
            buttonContent = <><FaCheck className="follow-btn__check" /> Following</>;
        }
    } else if (isPending) {
        buttonContent = "Pending";
        buttonClass += " follow-btn--pending";
    } else if (followMode === "APPROVAL") {
        buttonContent = "Request to Follow";
    } else {
        buttonContent = "Follow";
    }

    return (
        <div className="follow">
            <button
                className={buttonClass}
                onClick={handleClick}
                disabled={isBusy || isPending}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {buttonContent}
            </button>
        </div>
    );
}

export default FollowButton;
